import type { ConfigurationOperation } from "@repo/app-types/entities/configurationAgent";
import type { KnowledgeBaseAttachment } from "@repo/app-types/entities/knowledgeBase";
import { buildKnowledgeBaseDocumentName } from "@repo/app-types/entities/knowledgeRetrievalTool";
import { isKnowledgeBaseDocumentOperation } from "@repo/app-types/helpers/configurationAgentParser";
import { useEffectEvent } from "@repo/ui/helpers/useEffectEvent";
import { useCallback, useMemo, useState, type FC, type ReactNode } from "react";
import {
  ConfigurationAssistantContext,
  type ConfigurationAssistantContextValue,
} from "../../../agents/configurationAssistant/configurationAssistantContext.js";

export interface KnowledgeBaseAssistantContextProviderProps {
  onApplyPatch: (patch: { find: string; replace: string }) => void;
  /** KB attachments for resolving slugified document names to display titles */
  knowledgeBaseAttachments?: readonly KnowledgeBaseAttachment[];
  children: ReactNode;
}

export const KnowledgeBaseAssistantContextProvider: FC<
  KnowledgeBaseAssistantContextProviderProps
> = ({ onApplyPatch, knowledgeBaseAttachments, children }) => {
  const [appliedOperationIds, setAppliedOperationIds] = useState(
    () => new Set<string>(),
  );

  const applyOperation = useEffectEvent(
    async (
      operationId: string,
      operation: ConfigurationOperation,
    ): Promise<void> => {
      if (appliedOperationIds.has(operationId)) {
        return;
      }
      if (!isKnowledgeBaseDocumentOperation(operation)) {
        throw new Error("Only KB document operations are supported");
      }
      onApplyPatch(operation.patch);
      setAppliedOperationIds((prev) => new Set(prev).add(operationId));
    },
  );

  const isOperationApplied = useCallback(
    (operationId: string) => appliedOperationIds.has(operationId),
    [appliedOperationIds],
  );

  const getDocumentDisplayName = useCallback(
    (slugifiedName: string): string | undefined => {
      const attachment = knowledgeBaseAttachments?.find(
        (a) =>
          buildKnowledgeBaseDocumentName({ title: a.title }) === slugifiedName,
      );
      return attachment?.title ?? attachment?.file_name ?? undefined;
    },
    [knowledgeBaseAttachments],
  );

  // Provide stubs for playbook-specific lookups that aren't relevant in the KB context
  const getEntityName = useCallback(() => undefined, []);

  const contextValue: ConfigurationAssistantContextValue = useMemo(
    () => ({
      applyOperation,
      isOperationApplied,
      getEntityName,
      getDocumentDisplayName,
    }),
    [applyOperation, isOperationApplied, getEntityName, getDocumentDisplayName],
  );

  return (
    <ConfigurationAssistantContext.Provider value={contextValue}>
      {children}
    </ConfigurationAssistantContext.Provider>
  );
};
